import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import {
  Home, ChevronDown, ChevronUp, Edit2, Save, X, AlertTriangle,
  KeyRound, ParkingSquare, DoorOpen, PawPrint, Bell, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../firebase';
import { useCompany } from '../../context/CompanyContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import PropertyDetailsEditor, { EMPTY_PROPERTY_DETAILS } from './PropertyDetailsEditor';

/**
 * PropertyDetailsCard — collapsible "Property & Access" card used on both
 * Lead and Client profile pages.
 *
 *  • Reads `leadOrClient.propertyDetails`
 *  • Saves via Firestore updateDoc on `companies/{cid}/leads/{id}` (leads and
 *    clients share the same collection in this codebase).
 *  • Edit access: any company member with lead-write Firestore permission.
 *
 * Pure UI; renders an empty hint state when no fields are populated so
 * operators always have a clear "add it now" path.
 */
export default function PropertyDetailsCard({ leadOrClient, defaultOpen = false }) {
  const { companyId } = useCompany();
  const id = leadOrClient?.id;
  const initial = leadOrClient?.propertyDetails || {};

  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_PROPERTY_DETAILS, ...initial });

  // Re-sync draft whenever the underlying lead doc swaps in.
  React.useEffect(() => {
    setDraft({ ...EMPTY_PROPERTY_DETAILS, ...(leadOrClient?.propertyDetails || {}) });
    setEditing(false);
  }, [leadOrClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!id || !companyId) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, 'companies', companyId, 'leads', id);
      // Trim string fields. Empty strings stay (Firestore-safe and lets ops
      // clear a field deliberately by saving blank).
      const cleaned = {
        flashNote:         (draft.flashNote || '').trim().slice(0, 120),
        gateCode:          (draft.gateCode || '').trim(),
        parkingNotes:      (draft.parkingNotes || '').trim(),
        entryInstructions: (draft.entryInstructions || '').trim(),
        petOnSite:         !!draft.petOnSite,
        petNotes:          (draft.petNotes || '').trim(),
        alarmInfo:         (draft.alarmInfo || '').trim(),
        siteHazards:       (draft.siteHazards || '').trim(),
      };
      await updateDoc(ref, {
        propertyDetails: cleaned,
        updatedAt: serverTimestamp(),
      });
      toast.success('Property details saved');
      setEditing(false);
    } catch (err) {
      console.error('[PropertyDetailsCard] Save failed:', err);
      toast.error(err?.message || 'Failed to save property details');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({ ...EMPTY_PROPERTY_DETAILS, ...(leadOrClient?.propertyDetails || {}) });
    setEditing(false);
  };

  const populatedRows = readModeRows(initial);
  const hasAny = populatedRows.length > 0;

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white"
      data-testid="property-details-card"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-gray-50 transition-colors rounded-t-lg"
        data-testid="property-details-toggle"
      >
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Property &amp; Access</span>
          {initial?.flashNote && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
              <AlertTriangle className="h-2.5 w-2.5" />
              Flash
            </span>
          )}
          {!hasAny && (
            <span className="text-[11px] text-gray-400 font-normal italic">empty</span>
          )}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          {!editing && (
            <>
              {hasAny ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {populatedRows.map((row) => (
                    <div
                      key={row.key}
                      className="flex items-start gap-2"
                      data-testid={`pd-row-${row.key}`}
                    >
                      <row.icon className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          {row.label}
                        </dt>
                        <dd className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                          {row.value}
                        </dd>
                      </div>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-gray-500">
                  No property or access details on file. Add gate codes, parking
                  notes, pet warnings, and a short flash note here so techs see
                  them on every visit.
                </p>
              )}
              <div className="pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  data-testid="property-details-edit-btn"
                >
                  <Edit2 className="h-3 w-3 mr-1.5" />
                  {hasAny ? 'Edit details' : 'Add details'}
                </Button>
              </div>
            </>
          )}

          {editing && (
            <>
              <PropertyDetailsEditor value={draft} onChange={setDraft} />
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={saving}
                  data-testid="property-details-cancel-btn"
                >
                  <X className="h-3 w-3 mr-1.5" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="property-details-save-btn"
                >
                  <Save className="h-3 w-3 mr-1.5" />
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Read-mode row helpers ─────────────────────────────────────────────────

function readModeRows(pd = {}) {
  const rows = [];
  const push = (key, label, value, icon) => {
    const v = typeof value === 'string' ? value.trim() : value;
    if (v === '' || v === null || v === undefined || v === false) return;
    rows.push({ key, label, value: v === true ? 'Yes' : v, icon });
  };
  // Order chosen to mirror the importance a tech actually needs at the door.
  push('flashNote',         'Flash Note',         pd.flashNote,         AlertTriangle);
  push('gateCode',          'Gate Code',          pd.gateCode,          KeyRound);
  push('entryInstructions', 'Entry Instructions', pd.entryInstructions, DoorOpen);
  push('parkingNotes',      'Parking',            pd.parkingNotes,      ParkingSquare);
  if (pd.petOnSite) {
    rows.push({
      key: 'petOnSite',
      label: 'Pet on Site',
      value: (pd.petNotes || '').trim() || 'Yes',
      icon: PawPrint,
    });
  }
  push('alarmInfo',         'Alarm',              pd.alarmInfo,         Bell);
  push('siteHazards',       'Site Hazards',       pd.siteHazards,       ShieldAlert);
  return rows;
}
